import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  SCOPE_ALL,
  SCOPE_UNASSIGNED,
  type CrossTenantMembership,
  type MerchantFilters,
  type MerchantListPage,
  type MerchantStatus,
  type MerchantStatusTransition,
} from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** Page size for the approval queue — matches the API's own default. */
export const MERCHANTS_PAGE_SIZE = 50;

/**
 * `GET /api/v1/admin/merchants` — every read on this domain is audited
 * with a stated purpose (`merchant-admin.dto.ts`'s own `reason` field,
 * ≥10 characters, mandatory even for a LIST). The query stays `enabled:
 * false` until the caller has actually typed one — there is no default
 * reason this app could invent on someone's behalf.
 *
 * The response is a bounded page since the P0 stabilisation lot. `offset`
 * is part of the query key so paging refetches; `keepPreviousData` avoids
 * a skeleton flash between pages.
 */
export function useMerchantsQuery(
  filters: MerchantFilters,
  reason: string,
  offset: number,
): UseQueryResult<MerchantListPage, ApiError> {
  const { accessToken } = useSession();
  const reasonValid = reason.trim().length >= 10;
  const search = filters.search.trim();

  return useQuery({
    queryKey: ['merchants', filters.status, search, filters.scope, reason, offset],
    queryFn: async () => {
      const params = new URLSearchParams({
        reason,
        limit: String(MERCHANTS_PAGE_SIZE),
        offset: String(offset),
      });
      if (filters.status !== '') params.set('status', filters.status);
      // A search under two characters is not sent (the API refuses it); the list simply stays unfiltered.
      if (search.length >= 2) params.set('search', search);
      // Narrowing by portfolio is a superadmin's tool; an administrator's scope is always `all` (its own, server side).
      if (filters.scope === SCOPE_UNASSIGNED) params.set('unassigned', 'true');
      else if (filters.scope !== SCOPE_ALL && filters.scope !== '')
        params.set('managedBy', filters.scope);
      return apiRequest<MerchantListPage>(`/admin/merchants?${params.toString()}`, {
        accessToken,
      });
    },
    enabled: accessToken !== null && reasonValid,
    placeholderData: (previous) => previous,
  });
}

/**
 * `PATCH /api/v1/admin/merchants/:id/status`. `confirmPassword` - the operator's OWN password, typed again - is required by the API
 * for the two critical targets (`suspended`, `refused`) and ignored for the others; it travels in the request body only, is consumed
 * by the API's re-authentication guard, and is never kept here (`gcTime: 0`: the variables do not outlive the call).
 */
export function useTransitionMerchantStatusMutation(): UseMutationResult<
  MerchantStatusTransition,
  ApiError,
  { tenantId: string; status: MerchantStatus; reason: string; confirmPassword?: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ tenantId, status, reason, confirmPassword }) =>
      apiRequest<MerchantStatusTransition>(`/admin/merchants/${tenantId}/status`, {
        method: 'PATCH',
        body: { status, reason, ...(confirmPassword === undefined ? {} : { confirmPassword }) },
        accessToken,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

/**
 * `POST /api/v1/admin/merchants/:id/owner/temporary-password` - a new provisional password for a shop's owner: from a SUPERADMIN (any
 * shop, decision D2) or from the administrator who MANAGES the shop while its owner has never signed in (decision M3, option B). The response carries the password: it is shown once by the caller and never cached (`gcTime: 0`).
 */
export function useResetOwnerPasswordMutation(): UseMutationResult<
  { temporaryPassword: string; expiresAt: string; ownerEmail?: string },
  ApiError,
  { tenantId: string; reason: string; confirmPassword: string }
> {
  const { accessToken } = useSession();
  return useMutation({
    gcTime: 0,
    mutationFn: ({ tenantId, reason, confirmPassword }) =>
      apiRequest<{ temporaryPassword: string; expiresAt: string; ownerEmail?: string }>(
        `/admin/merchants/${tenantId}/owner/temporary-password`,
        { method: 'POST', body: { reason, confirmPassword }, accessToken },
      ),
  });
}

/**
 * `GET /api/v1/admin/users/:userId/memberships` — a lookup, not a list
 * this page owns on load: a mutation-shaped hook (submit userId + reason,
 * read the result once) fits the real one-off-audited-read the API
 * actually is, better than a `useQuery` keyed on a userId nobody typed yet.
 */
export function useUserMembershipsLookupMutation(): UseMutationResult<
  { memberships: readonly CrossTenantMembership[] },
  ApiError,
  { userId: string; reason: string }
> {
  const { accessToken } = useSession();
  return useMutation({
    mutationFn: ({ userId, reason }) => {
      const params = new URLSearchParams({ reason });
      return apiRequest<{ memberships: readonly CrossTenantMembership[] }>(
        `/admin/users/${userId}/memberships?${params.toString()}`,
        { accessToken },
      );
    },
  });
}
